import GameAutostart from "../GameAutostart";
import events from "../../events/EventsObservable";
import GameFreespins from "../GameFreespins";
import { STATES } from "../../states/states";
import SystemConfig, { CONFIG_PARAMS, MODES } from "../../system/SystemConfig";
import MadmaxError, { errorFactroy } from "../../error/MadmaxError";
import { ERROR_CODES } from "../../error/errorCodes";
import GameRound from "../GameRound";
import Wallet from "../../wallet/Wallet";
import { inject, injectable } from "inversify";
import SERVICE_IDENTIFIER from "../../constants/identifiers";
import Connect from "../../connect/Connect";
import InitResult from "../../connect/result/InitResult";
import AddResult from "../../connect/result/AddResult";
import { EVENTS } from "../../events/baseEvents";
import IGame from "../IGame";
import IStatesManager from "../../states/IStatesManager";
import { GAME_EVENTS } from "../gameEvents";
import GameSlotsCore from "../GameSlotsCore";
import { ACTION_EVENTS } from "../../ui/UserAction";
import PostMessages from "../../system/PostMessages";
import { server_result } from "../../engine/madnes/madnes_variables";

@injectable()
export default class GameSlots implements IGame {
	private config: SystemConfig;
	private gameset: any;
	private statesManager: IStatesManager;
	private wallet: Wallet;
	private connect: Connect;
	private iforium: PostMessages;

	public round: GameRound;

	private addResult: AddResult;
	private endRoundConnectData: any;

	public freespins: GameFreespins;
	public autostart: GameAutostart;
	public core: GameSlotsCore;

	private forcespin: boolean;
	private _regulationGameEnabled: boolean;
	// private _addError: any;
	private _silentClose: boolean;
	private _hand: number;

	// if false, wallet.setWin(0) is never called and the game itself must clear win
	private _clearWin: boolean = true;

	constructor(
		@inject(SERVICE_IDENTIFIER.StatesManager) statesManager: IStatesManager,
		@inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
		@inject(SERVICE_IDENTIFIER.Wallet) wallet: Wallet,
		@inject(SERVICE_IDENTIFIER.Connect) connect: Connect,
		@inject(SERVICE_IDENTIFIER.GameCore) core: GameSlotsCore,
		@inject(SERVICE_IDENTIFIER.PostMessages) iforium: PostMessages
	) {
		console.log("game slots constructor");
		this.gameset = config.gameset;
		this.statesManager = statesManager;
		this.config = config;
		this.wallet = wallet;
		this.connect = connect;
		this.core = core;
		this.iforium = iforium;

		this.round = new GameRound();

		this.autostart = new GameAutostart(config);

		this.freespins = new GameFreespins(config, connect);

		this.forcespin = false;
		this._regulationGameEnabled = true;
		// this._addError = null;
		this._silentClose = false;
		this._hand = 1;

		events.subscribe(EVENTS.INIT_SUCCESS, (eventName, initResult) =>
			this.onInitSuccess(initResult)
		);
		events.subscribe(
			ACTION_EVENTS.ACTION_HANDEDNESS_TOGGLE,
			(eventName, data) => this.onChangeHand(data)
		);
	}

	get gameCore(): GameSlotsCore {
		return this.core;
	}

	/**
	 * @param {InitResult} initResult
	 */
	private onInitSuccess(initResult: InitResult) {
		let lastRound = initResult.getLastRound && initResult.getLastRound();

		if (lastRound && lastRound.roundId != null) {
			let rid = lastRound.roundId + 1;
			if (lastRound.ended != null && !lastRound.ended) {
				this.round.setUnfinished(true);
				rid = lastRound.roundId;

				let freespinsProgress = initResult.getFreeSpinsProgress();
				if (freespinsProgress != null) {
					this.freespins.setUnfinishedData(freespinsProgress);
				}
			}

			this.round.setId(rid);
		}
	}

	create() {
		this.core.create();
	}

	regulationEnableGame(bool = true) {
		this._regulationGameEnabled = bool;
	}

	isEnabled() {
		return this._regulationGameEnabled;
	}

	isAutoRun() {
		return (
			this.autostart?.isAutostart() ||
			(this.freespins.isInFreeSpins() && this.freespins.isFreespinsAutorun()) ||
			// allow forcespin if not bonus and allow forcespin if bonus and has freespins
			(this.forcespin && !this.wallet.isBonus()) ||
			(this.forcespin && this.wallet.isBonus() && this.wallet.getFreeSpins())
		);
	}

	setForcespin(bool: boolean = true) {
		this.forcespin = bool;
	}

	forcestop() {
		this.core.forcestop();
	}

	isInFreeSpins() {
		return this.freespins && this.freespins.isInFreeSpins();
	}

	async start() {
		// this.addWinToBalance();
		try {
			await this._checkStart();
		} catch (e) {
			return await Promise.reject(e);
		}
		await this.statesManager.setState(STATES.SPIN);

		let promise = events.dispatch(EVENTS.BEFORE_SPIN).then(() => {
			this.wallet.updateBalanceUI();
			this.wallet.isBonus() && this.wallet.removeBet();
			this._clearWin && this.wallet.setWin(0);
		});

		this.setForcespin(false);

		!this.isInFreeSpins() && this.autostart?.resetLastAutostart();
		this.autostart?.isAutostart() && this.autostart?.decAutostart();

		this.round.setRunning(true);
		this.round.setEndRoundEnabled(false);
		// this._addError = null;

		let params = {
			serverApi: this.config.get(CONFIG_PARAMS.SERVER_API),
			playerId: this.config.get(CONFIG_PARAMS.PLAYER_ID),
			casinoId: this.config.get(CONFIG_PARAMS.CASINO_ID),
			sessionId: this.config.get(CONFIG_PARAMS.SESSION_ID),
			roundId: this.round.getId(),
			internal: this.config.get(CONFIG_PARAMS.INTERNAL),
			linesCount: this.wallet.getLinesCount(),
			atomicBet: this.wallet.getBet(),
			coin: this.wallet.getCoin(),
			hand: this._hand,
			autostart: this.isAutoRun(),
			isBonus: this.wallet.isBonus(),
		};

		this.round.setBalanceTmp(this.wallet.getBalance());

		await events.dispatch(GAME_EVENTS.GAME_START_ROUND);

		let spin = this.core.start();
		this.addResult = null;

		try {
			if (this.config.get(CONFIG_PARAMS.MODE) === MODES.REPLAY) {
				// joker27+ eats linewins, do a deep copy of result to fix this
				let data = JSON.parse(
					JSON.stringify(this.config.get(CONFIG_PARAMS.REPLAY_RESULT).getData())
				);
				this.addResult = new AddResult(data);
			} else {
				this.addResult = await this.connect.add(params);
			}
		} catch (error) {
			spin.then((res) => this.round.setRunning(false));
			return this._onAddError(error);
		}
		await promise;
		this._onAddSuccess(this.addResult);

		return spin.then(() => Promise.resolve(this.addResult));
	}

	private _onAddSuccess(addResult: AddResult) {
		// this.round.setId(addResult.getRoundId());
		this.round.setEndRoundEnabled(true);
		this.round.resetSkippedIds();

		let iforium = addResult.getWindowMessage();
		if (iforium && this.iforium.isActive()) {
			this.iforium.handleWalletMessage(iforium);
		}

		this.config.set(CONFIG_PARAMS.GAME_SPECIFICS, addResult.getGameSpecifics());
		this.config.set(
			CONFIG_PARAMS.FS_GAME_SPECIFICS,
			addResult.getFSGameSpecifics()
		);
		this._silentClose = addResult.isSilentClose();

        this.wallet.setFSTotalWin(addResult.getFSTotalWin())
        this.wallet.setBalance(this.wallet.isBonus() ? addResult.getBalance():addResult.getBalance());

		let resultIndex = this.round.getResultIndex() || 0;
		this.core.stop(addResult, resultIndex);
		events.dispatch(GAME_EVENTS.GAME_ADD_SUCCESS);
	}

	async _onAddError(error) {
		if (error instanceof MadmaxError) {
			// this._addError = error;

			let failResult = error.getFailResult();
			if (failResult) {
				this.core.stopFail(failResult);
			}
		}

		return Promise.reject(error);
	}

	_checkStart() {
		if (this.round.isRunning()) {
			return Promise.reject(errorFactroy({ code: ERROR_CODES.GAME_RUNNING }));
		}

		if (!this.isEnabled()) {
			return Promise.reject(
				errorFactroy({ code: ERROR_CODES.GAME_NOT_ENABLED })
			);
		}

		if (
			this.config.get(CONFIG_PARAMS.MODE) !== MODES.REPLAY &&
			!this.wallet.checkBalance()
		) {
			events.dispatch(EVENTS.LOW_CREDIT);
			if (!this.iforium.isActive()) {
				return Promise.reject(
					errorFactroy({ code: ERROR_CODES.GAME_NOT_ENOUGH_CREDIT })
				);
			}
		}

		return Promise.resolve();
	}

	async respin(addResult) {
		this.round.incResultIndex();

		let spin = this.core.start();

		setTimeout(() => {
			let resultIndex = this.round.getResultIndex() || 0;
			this.gameCore.stop(addResult, resultIndex);
		}, 100);

		return spin;
	}

	async spinFreespin() {
		this.round.setRunning(true);

		this.setForcespin(false);

		this.statesManager.setState(STATES.SPIN);

		this.freespins.incRoundIndex();

		let spin = this.core.start();
		await new Promise((r) => setTimeout(r, 200));
		let addResult = this.freespins.getActualResult();

		//onSpinFreespinSuccess
		this.config.set(CONFIG_PARAMS.GAME_SPECIFICS, addResult.getGameSpecifics());
		this.config.set(
			CONFIG_PARAMS.FS_GAME_SPECIFICS,
			addResult.getFSGameSpecifics()
		);

		this.core.stopFreespins(addResult);

		return spin.then(() => Promise.resolve(addResult));
	}

	async endRound() {
		if (!this.round.isEndRoundEnabled()) {
			console.log("end round not enabled");
			return;
		}

		// if(this._addError) {
		//     return this._endRoundError(this._addError)
		// }

		if (this.freespins.isInFreeSpins()) {
			return this.endRoundFreeSpins();
		}

		this._checkWin();
		//this.round.setBalanceTmp(null);

		return this._endRoundConnect().then((data) => this._onEndRound(data));
	}

	async _endRoundConnect() {
		if (this._silentClose) {
			return;
		}

		let params = {
			serverApi: this.config.get(CONFIG_PARAMS.SERVER_API),
			casinoId: this.config.get(CONFIG_PARAMS.CASINO_ID),
			sessionId: this.config.get(CONFIG_PARAMS.SESSION_ID),
			roundId: this.round.getId(),
			ended: true,
			reels_stop_enforced: this.core.slots
				? this.core.slots.reels.isForceStop()
				: false,
		};

		return this.connect.end(params);
	}

	// /**
	//  * @param {MadmaxError} error
	//  * @return {Promise<never>}
	//  * @private
	//  */
	// _endRoundError(error) {
	//     this.autostart?.stopAutostart();
	//     this.round.setRunning(false);
	//     return Promise.reject(error);
	// }

	endRoundFreeSpins() {
		let lastRound = this.freespins.isLastRound();

		return this._endRoundFreeSpinsConnect(lastRound).then((data) => {
			return events
				.dispatch(GAME_EVENTS.GAME_FREESPIN_END, {
					ended: lastRound,
					autostart: this.autostart?.isAutostart(),
				})
				.then(() => {
					this.freespins.onEndRound();

					if (lastRound) {
						this._checkWin();

						this.freespins.end();
						return events
							.dispatch(GAME_EVENTS.GAME_END_ROUND_FREESPINS, {
								ended: lastRound,
							})
							.then(() => this._onEndRound(data));
					}
				})
				.then(() => {
					this.round.setRunning(false);
				});
		});
	}

	async _endRoundFreeSpinsConnect(ended) {
		if (this._silentClose) {
			return;
		}

		let params = {
			serverApi: this.config.get(CONFIG_PARAMS.SERVER_API),
			casinoId: this.config.get(CONFIG_PARAMS.CASINO_ID),
			sessionId: this.config.get(CONFIG_PARAMS.SESSION_ID),
			roundId: this.round.getId(),
			fsWin: this.freespins.getWin(),
			fsOrder: this.freespins.getRoundNumber(),
			fsCount: this.freespins.getFreeSpinNumberTotal(),
			ended: ended,
			reels_stop_enforced: this.core.slots
				? this.core.slots.reels.isForceStop()
				: false,
		};

		return this.connect.end(params);
	}

	async _onEndRound(data: any) {
		let bonus = this.addResult.getBonus();
		if (bonus) {
			let currentFs = this.wallet.getFreeSpins();
			if (bonus.rounds > currentFs) {
				events.dispatch(GAME_EVENTS.GAME_NEW_FREESPINS, {
					rounds: bonus.rounds - currentFs,
				});
			}
			this.wallet.setBonusData(bonus);
		} else {
			if (this.wallet.isBonus()) {
				this.wallet.setBonusData({ bet: this.wallet.getBonusBet(), rounds: 0 });
				// events.dispatch(MESSAGE_EVENTS.SWITCH_TO_REAL);
			}
		}

		this.round.incId();
		this.round.resetResultIndex();

		this.round.setRunning(false);

		this.endRoundConnectData = data;

		let ret = await events.dispatch(GAME_EVENTS.GAME_END_ROUND);

		return ret;
	}

	addWinToBalance(data: any = this.endRoundConnectData) {
		if (
			this.config.get(CONFIG_PARAMS.MODE) === MODES.FREE ||
			this._silentClose ||
			!(data && data.data)
		) {
			this.wallet.addWinToBalance();
		} else {
			this.wallet.setBalanceSilent(data.data.balance);
		}
	}

	private _checkWin() {
		let walletWin = this.wallet.getWin();
		let totalWin = this.addResult.getTotalWin();

		if (walletWin != totalWin) {
			console.warn("WARNING: wallet win != result.totalWin");
		}
	}

	onChangeHand(data) {
		if (data === "right") {
			this._hand = 1;
		} else if (data === "left") {
			this._hand = 2;
		} else {
			this._hand = 0;
		}
	}

	setClearWin(val: boolean) {
		this._clearWin = val;
	}

	getClearWin() {
		return this._clearWin;
	}
}
